'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import {
  Search, X, Briefcase, FileText, User,
  MessageSquare, Loader2, Clock, TrendingUp,
} from 'lucide-react';
import { Avatar } from '@/components/ds';

interface SearchResult {
  jobs: Array<{ id: string; title: string; company: string; job_type: string; rate: string }>;
  users: Array<{ id: string; display_name: string; username: string; title: string; avatar_url: string | null }>;
  posts: Array<{ id: string; content: string; created_at: string; author: { display_name: string; username: string } | null }>;
  messages: Array<{ id: string; content: string; conversation_id: string; created_at: string }>;
}

interface SearchModalProps {
  onClose: () => void;
}

const RECENT_KEY = 'indiestry_recent_searches';

export default function SearchModal({ onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_KEY);
      if (stored) setRecentSearches(JSON.parse(stored).slice(0, 5));
    } catch {}
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const saveRecentSearch = (q: string) => {
    try {
      const existing = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      const updated = [q, ...existing.filter((s: string) => s !== q)].slice(0, 5);
      localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
      setRecentSearches(updated);
    } catch {}
  };

  const doSearch = useCallback(async (q: string) => {
    if (!q || q.length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`);
      if (res.status === 401) {
        setError('Please sign in to search');
        return;
      }
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setResults(data);
    } catch (err) {
      console.error('Search error:', err);
      setError('Search unavailable. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, doSearch]);

  const navigate = (url: string) => {
    if (query.trim()) saveRecentSearch(query.trim());
    router.push(url);
    onClose();
  };

  const hasResults = results && (
    results.jobs.length > 0 ||
    results.users.length > 0 ||
    results.posts.length > 0 ||
    results.messages.length > 0
  );

  const totalResults = results
    ? results.jobs.length + results.users.length + results.posts.length + results.messages.length
    : 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center pt-16 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-surface-1 border border-border-subtle rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border-subtle">
          {loading
            ? <Loader2 className="w-5 h-5 text-text-secondary animate-spin flex-shrink-0" />
            : <Search className="w-5 h-5 text-text-secondary flex-shrink-0" />
          }
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search jobs, people, posts, messages..."
            className="flex-1 bg-transparent text-text-primary placeholder:text-text-tertiary
              focus:outline-none text-base"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="text-text-tertiary hover:text-text-secondary transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="text-text-tertiary hover:text-text-secondary text-xs border border-border-subtle
              rounded px-1.5 py-0.5 transition-colors"
          >
            Esc
          </button>
        </div>

        {/* Results area */}
        <div className="max-h-[70vh] overflow-y-auto">

          {/* Error */}
          {error && (
            <div className="px-4 py-8 text-center">
              <p className="text-status-busy text-sm">{error}</p>
            </div>
          )}

          {/* No results */}
          {!loading && !error && query.length >= 2 && results && !hasResults && (
            <div className="px-4 py-12 text-center">
              <Search className="w-10 h-10 text-text-tertiary mx-auto mb-3" />
              <p className="text-text-secondary text-sm">No results for &ldquo;{query}&rdquo;</p>
              <p className="text-text-tertiary text-xs mt-1">Try different keywords or check spelling</p>
            </div>
          )}

          {/* Recent searches — shown when no query */}
          {!query && recentSearches.length > 0 && (
            <div className="py-3">
              <p className="px-4 text-xs text-text-tertiary font-medium uppercase tracking-wide mb-2">
                Recent searches
              </p>
              {recentSearches.map(s => (
                <button
                  key={s}
                  onClick={() => setQuery(s)}
                  className="w-full flex items-center gap-3 px-4 py-2.5
                    hover:bg-surface-2 transition-colors text-left"
                >
                  <Clock className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                  <span className="text-text-secondary text-sm">{s}</span>
                </button>
              ))}
            </div>
          )}

          {/* Quick links — shown when no query and no recent */}
          {!query && recentSearches.length === 0 && (
            <div className="p-4">
              <p className="text-xs text-text-tertiary font-medium uppercase tracking-wide mb-3">
                Quick links
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Browse Jobs', url: '/jobs', icon: <Briefcase className="w-4 h-4" /> },
                  { label: 'Community Posts', url: '/posts', icon: <FileText className="w-4 h-4" /> },
                  { label: 'Messages', url: '/messages', icon: <MessageSquare className="w-4 h-4" /> },
                  { label: 'Browse Tags', url: '/tags', icon: <TrendingUp className="w-4 h-4" /> },
                ].map(link => (
                  <button
                    key={link.url}
                    onClick={() => navigate(link.url)}
                    className="flex items-center gap-2.5 p-3 rounded-xl bg-surface-2
                      hover:bg-surface-3 transition-colors text-text-secondary hover:text-text-primary text-sm"
                  >
                    <span className="text-text-secondary">{link.icon}</span>
                    {link.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          {hasResults && (
            <div className="py-2">
              <p className="px-4 py-2 text-xs text-text-tertiary">
                {totalResults} result{totalResults !== 1 ? 's' : ''} for &ldquo;{query}&rdquo;
              </p>

              {/* Jobs */}
              {results!.jobs.length > 0 && (
                <div>
                  <p className="px-4 py-1.5 text-xs text-text-tertiary font-medium uppercase tracking-wide bg-surface-2">
                    Jobs
                  </p>
                  {results!.jobs.map(job => (
                    <button
                      key={job.id}
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      className="w-full flex items-center gap-3 px-4 py-3
                        hover:bg-surface-2 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-surface-3 border border-border-subtle
                        flex items-center justify-center flex-shrink-0">
                        <Briefcase className="w-4 h-4 text-text-secondary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-text-primary text-sm font-medium truncate">{job.title}</p>
                        <p className="text-text-tertiary text-xs truncate">
                          {job.company}
                          {job.rate ? ` · ${job.rate}` : ''}
                          {job.job_type ? ` · ${job.job_type}` : ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Users */}
              {results!.users.length > 0 && (
                <div>
                  <p className="px-4 py-1.5 text-xs text-text-tertiary font-medium uppercase tracking-wide bg-surface-2">
                    People
                  </p>
                  {results!.users.map(u => (
                    <button
                      key={u.id}
                      onClick={() => navigate(`/u/${u.username}`)}
                      className="w-full flex items-center gap-3 px-4 py-3
                        hover:bg-surface-2 transition-colors text-left"
                    >
                      <Avatar
                        src={u.avatar_url}
                        name={u.display_name || u.username || 'User'}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-text-primary text-sm font-medium truncate">{u.display_name}</p>
                        <p className="text-text-tertiary text-xs truncate">
                          @{u.username}
                          {u.title ? ` · ${u.title}` : ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Posts */}
              {results!.posts.length > 0 && (
                <div>
                  <p className="px-4 py-1.5 text-xs text-text-tertiary font-medium uppercase tracking-wide bg-surface-2">
                    Posts
                  </p>
                  {results!.posts.map(post => (
                    <button
                      key={post.id}
                      onClick={() => navigate('/posts')}
                      className="w-full flex items-center gap-3 px-4 py-3
                        hover:bg-surface-2 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-surface-3 border border-border-subtle
                        flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-text-secondary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-text-primary text-sm truncate">
                          {post.content.slice(0, 80)}{post.content.length > 80 ? '...' : ''}
                        </p>
                        <p className="text-text-tertiary text-xs">
                          {post.author?.display_name || 'Unknown'} · {new Date(post.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Messages */}
              {results!.messages.length > 0 && (
                <div>
                  <p className="px-4 py-1.5 text-xs text-text-tertiary font-medium uppercase tracking-wide bg-surface-2">
                    Your messages
                  </p>
                  {results!.messages.map(msg => (
                    <button
                      key={msg.id}
                      onClick={() => navigate(`/messages/${msg.conversation_id}`)}
                      className="w-full flex items-center gap-3 px-4 py-3
                        hover:bg-surface-2 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-surface-3 border border-border-subtle
                        flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-4 h-4 text-text-secondary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-text-primary text-sm truncate">
                          {msg.content.slice(0, 80)}{msg.content.length > 80 ? '...' : ''}
                        </p>
                        <p className="text-text-tertiary text-xs">
                          {new Date(msg.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-border-subtle flex items-center justify-between">
          <p className="text-text-tertiary text-xs">Press Esc to close</p>
          {query.length >= 2 && !loading && (
            <p className="text-text-tertiary text-xs">
              Searching jobs, people, posts &amp; your messages
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
